import React from 'react';
import Appearance from 'styles/Appearance.js';
import Button from 'views/Button.js';
import Utils from 'files/Utils.js';

const GRACI = () => {

    const features = [
        'Instant answers on every Aqua Link product',
        'Step-by-step troubleshooting for your exact setup',
        'Included with your network purchase',
    ];

    // Placeholder until GRACI chat is wired into the storefront
    const handleAsk = () => {
        alert('Placeholder that would open a GRACI AI conversation. GRACI lives in the Aqua Link network and is being extended to this site for product Q&A.');
    };

    const headerBlock = (
        <div>
            <span style={{
                ...Appearance.textStyles.label(),
                marginBottom: 14,
                display: 'block',
            }}>
                {'Meet GRACI'}
            </span>
            <h2 style={{
                ...Appearance.textStyles.sectionHeading({ textAlign: 'left' }),
                marginBottom: Utils.isMobile() ? 0 : 16,
            }}>
                {'Your 24/7 AI safety expert'}
            </h2>
        </div>
    );

    const bodyBlock = (
        <div>
            <p style={{
                ...Appearance.textStyles.body(),
                marginBottom: 28,
                maxWidth: 480,
            }}>
                {'GRACI is trained on everything Aqua Link — every product, spec, and fix. Ask a question, describe a problem, and get an accurate answer instantly. It\'s one of the core benefits of owning the network.'}
            </p>

            {/* Feature highlights */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                marginBottom: 32,
            }}>
                {features.map((feature, index) => (
                    <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                    }}>
                        <i className={'fas fa-check-circle'} style={{
                            fontSize: 18,
                            color: Appearance.colors.primary(),
                        }} />
                        <span style={Appearance.textStyles.body({ color: Appearance.colors.text(), fontWeight: 500 })}>
                            {feature}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );

    const contentSection = (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
        }}>
            {headerBlock}
            {bodyBlock}
        </div>
    );

    const chatSection = (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
        }}>
            {/* Soft glow */}
            <div style={{
                position: 'absolute',
                width: Utils.isMobile() ? 300 : 420,
                height: Utils.isMobile() ? 300 : 420,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${Utils.hexToRGBA(Appearance.colors.primary(), 0.14)} 0%, rgba(255,255,255,0) 70%)`,
                pointerEvents: 'none',
            }} />

            {/* Chat window */}
            <div style={{
                width: '100%',
                maxWidth: 420,
                backgroundColor: Appearance.colors.white,
                borderRadius: 24,
                border: `1px solid ${Appearance.colors.border()}`,
                boxShadow: Appearance.boxShadow.cardHover,
                overflow: 'hidden',
                position: 'relative',
            }}>
                {/* Chat header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '18px 20px',
                    borderBottom: `1px solid ${Appearance.colors.border()}`,
                }}>
                    <div style={{
                        width: 42,
                        height: 42,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                    }}>
                        <img 
                        src={'/images/graci-ai-icon.png'}
                        alt="GRACI AI Icon"
                        style={{ width: '100%', height: '100%' }}
                        />
                    </div>
                    <div style={{ flexGrow: 1 }}>
                        <span style={{
                            ...Appearance.textStyles.body({ fontWeight: 700, color: Appearance.colors.text(), fontSize: 15 }),
                            display: 'block',
                        }}>
                            {'GRACI'}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{
                                width: 7,
                                height: 7,
                                borderRadius: 4,
                                backgroundColor: Appearance.colors.success,
                                animation: 'pulse 2s ease-in-out infinite',
                            }} />
                            <span style={Appearance.textStyles.caption({ fontSize: 12 })}>
                                {'AI Safety Assistant · Online'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Messages */}
                <div style={{
                    padding: 20,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 14,
                    backgroundColor: Appearance.colors.offWhite,
                }}>
                    {/* User message */}
                    <div style={{
                        alignSelf: 'flex-end',
                        maxWidth: '82%',
                        padding: '12px 16px',
                        borderRadius: 16,
                        borderBottomRightRadius: 4,
                        backgroundColor: Appearance.colors.primary(),
                    }}>
                        <span style={Appearance.textStyles.body({ color: Appearance.colors.white, fontSize: 14 })}>
                            {'My water sensor sent me an alert about a leak, what should I do?'}
                        </span>
                    </div>

                    {/* GRACI reply */}
                    <div style={{
                        alignSelf: 'flex-start',
                        maxWidth: '88%',
                        padding: '12px 16px',
                        borderRadius: 16,
                        borderBottomLeftRadius: 4,
                        backgroundColor: Appearance.colors.white,
                        border: `1px solid ${Appearance.colors.border()}`,
                    }}>
                        <span style={Appearance.textStyles.body({ color: Appearance.colors.text(), fontSize: 14 })}>
                            {'It looks like your water sensor has detected a potential leak. First, check the area around the sensor for any visible signs of water. If you find a leak, turn off the main water supply to prevent further damage. Next, inspect the sensor and the water connections to ensure they are secure and functioning properly.'}
                        </span>
                    </div>
                </div>

                {/* Input bar */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '14px 16px',
                    borderTop: `1px solid ${Appearance.colors.border()}`,
                    backgroundColor: Appearance.colors.white,
                }}>
                    <div style={{
                        flexGrow: 1,
                        padding: '10px 16px',
                        borderRadius: 20,
                        backgroundColor: Appearance.colors.offWhite,
                    }}>
                        <span style={Appearance.textStyles.body({ color: Appearance.colors.lightGrey, fontSize: 14 })}>
                            {'Ask GRACI anything…'}
                        </span>
                    </div>
                    <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: 20,
                        backgroundColor: Appearance.colors.primary(),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <i className={'fas fa-paper-plane'} style={{ fontSize: 14, color: Appearance.colors.white }} />
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <section id={'graci'} style={{
            width: '100%',
            backgroundColor: Appearance.colors.white
        }}>
            <div style={{
                maxWidth: Appearance.layout.maxWidth,
                margin: '0 auto',
                padding: Appearance.layout.sectionPadding(),
                display: 'grid',
                gridTemplateColumns: Utils.isMobile() ? '1fr' : '1fr 1fr',
                gap: Utils.isMobile() ? 32 : 80,
                alignItems: 'center',
            }}>
                {Utils.isMobile() ? (
                    <>
                        {headerBlock}
                        {chatSection}
                        {bodyBlock}
                    </>
                ) : (
                    <>
                        {contentSection}
                        {chatSection}
                    </>
                )}
            </div>
        </section>
    );
};

export default GRACI;
